import { ChangeDetectorRef, Component, HostBinding, OnDestroy, ViewChild } from '@angular/core';
import { SeConstraintNotifierService } from '@se/se.platform.base';
import { RasterImgWrapper, IAnatomicMap, IColorCode, IColorData, IFixedSize, IGuid, ImageType, IMultistageAnatomicMap, IMultistageColorData, MultiStageMapContainerComponent, IMultiStageContainerConfig } from '@se/ui.anatomic.map';
import { DataBinding, Guid, Logger } from '@se/se.platform.base';
import { EMPTY, forkJoin, lastValueFrom, Observable, of, Subscription } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { CpEntity, CpEntityList, ICpEntity, PropertyType } from '../../cp-configuration/view-model';
import { CpMultiStageMapContainerControl } from '../../cp-configuration/view-model/cp-multistage-container-control';
import { CpDatasourceService } from '../../cp-datasource.service';
import { CpModelService } from '../../cp-model.service';
import { CpEffects, EffectResult, EffectResultImage, EffectResultImageDelete, EventResult, ObservableOrAwaitable } from '../../store/cp.effects';
import { AbstractControlComponent } from '../abstract-control';
import { CpMultiStageMapParsers } from './cp-multistage-parsers.service';
import { MultiStageMapLoaderService } from './multistageMapsLoader.service';
import { ICpDescription } from './models/ICpDescription';
import { DataBindingPropertyType, IDataSourceItem, MultistageMapLayoutMode, isDataBindingProperty } from '@se/cp.common';
import { IFixedLayout, ILayoutConfiguration, IUserSelection } from '@se/cp.usercontrols/cp-uc-ecostress-map/cp-uc-ecostress-map.component';



@Component({
    selector: 'cp-multistage-map-wrapper',
    templateUrl: './multistage-map-wrapper.component.html',
    styleUrls: ['./multistage-map-wrapper.component.scss'],
    standalone: false
})
export class CpMultiStageMapWrapperComponent extends AbstractControlComponent implements OnDestroy {
    control!: CpMultiStageMapContainerControl;

    public multistageConfig: IMultiStageContainerConfig | undefined;
    public stagesColorData: IMultistageColorData[] = [];
    public colorLegend: IColorCode[] = [];
    public fixedSize: IFixedSize | null = null;
    public fixedLayout: IFixedLayout | null = null;

    private layoutConfig: ILayoutConfiguration[] | undefined;
    private stageEntityList!: CpEntityList;
    private viewsEntityList!: CpEntityList;
    private containerSub!: Subscription;
    private allRegionsDataDescription: ICpDescription[] = [];
    private currentViewSet: string = '';
    private guidS: IGuid[] = [];
    private guidSToDelete: IGuid[] = [];
    private stagesIDtoPrint: string[] | undefined;
    private stagesIDNotToPrint: string[] | undefined;

    private _isLoading = false;
    public get isLoading() {
        return this._isLoading;
    }
    public _viewsSideLenght: number = 0;
    selectedMap: any;
    selectedSegment: any;
    fixedColumnNumber: number = 0;
    viewsSideLenght = 61.5 * 3 + 10 + 4;
    _viewTopLabel: string | null = null;
    _viewBottomLabel: string | null = null;

    private get mapLayoutMode(): MultistageMapLayoutMode {
        if (this.control.layoutMode == undefined) {
            return MultistageMapLayoutMode.VIEWSET_PER_STAGE;
        }

        return this.control.layoutMode;
    }

    @ViewChild(MultiStageMapContainerComponent, { static: false }) public multistagecontainerComponent!: MultiStageMapContainerComponent;

    @HostBinding('style')
    get dimensions() {
        return {
            height: this._viewsSideLenght + 'px',
        };
    }

    @HostBinding('class.multistage-wrapper-hidden')
    get _isMapHidden(): boolean {
        return !!this.isHidden;
    }

    constructor(
        private cpEffects: CpEffects,
        private dataSourceService: CpDatasourceService,
        private modelService: CpModelService,
        private mapLoaderService: MultiStageMapLoaderService,
        private parsers: CpMultiStageMapParsers,
        cdr: ChangeDetectorRef,
        constraintNotifier: SeConstraintNotifierService,
        logger: Logger,
    ) {
        super(cdr, constraintNotifier, logger);
        this.multistageConfig = this.createContainer('', [], []);
    }

    // requested by parent abstract class
    protected onModelChange(): boolean {
        return true;
    }

    protected handleHiddenNotification(isHidden: boolean) {
        this.isHidden = isHidden;
    }

    _isMapDisabled: boolean = false;
    set isMapDisabled(value: boolean) {
        this._isMapDisabled = value;
    }
    get isMapDisabled() {
        return this._isMapDisabled;
    }

    protected handleDisabledNotification(isDisabled: boolean) {
        this.isMapDisabled = isDisabled || this.isDisabled;
    }

    protected async init() {
        this._isLoading = true;
        this.initMapContainerGraphicLayout();
        this.initColorLegend();
        this.initRegionsDataDescription();
        this.setViewLabels();
        this.initMapForLayoutMode();
    }

    private initColorLegend() {
        this.colorLegend = this.dataSourceService
                .getById(this.control.legendDataSource!)
                ?.map((item) => this.parsers.parseColor(item)) || [];
    }

    private initRegionsDataDescription() {
        if (this.control?.segmentsDataSource) {
            this.allRegionsDataDescription = this.dataSourceService
                    .getById(this.control.segmentsDataSource)
                    ?.map((desc) => this.parsers.parseRegionDescription(desc)) || [];
        }
    }

    private initMapForLayoutMode() {
        if (this.mapLayoutMode === MultistageMapLayoutMode.VIEWSET_PER_STAGE) {
            this.initDefaultConfig();
            this.registerToCpEffect();
        }

        if (this.mapLayoutMode === MultistageMapLayoutMode.DYNAMIC_LAYOUT) {
            this.initDynamicLayoutConfig();
        }
    }

    // configurazione per MultistageConfig
    private initDefaultConfig() {
        this.registerMultiStageBinding();
        this.registerViewSetBindingChange();
    }

    // configurazione per EcostressConfig
    private initDynamicLayoutConfig() {
        this.registerEcostressBinding();
        this.registerEcostressViewBinding();
        this.registerEcostressViewSetBinding();
    }

    private registerMultiStageBinding() {
        this.modelService
            .registerList(this.control.dataBinding || new DataBinding(), true)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(async (entity: CpEntityList | undefined) => {
                if (!entity) {
                    return;
                }
                this.stageEntityList = entity;
                this.createModelColorData();
                if (this.stageEntityList.activeEntities.length > 0) {
                    const views: IMultistageAnatomicMap[] = await this.initAnatomicMapViews();
                    this.multistageConfig = this.createContainer(this.control.name, views, this.stagesColorData);
                    this._isLoading = false;
                }
            });
    }

    private registerViewSetBindingChange() {
        this.modelService
            .register(this.control.viewSetBinding || new DataBinding())
            .pipe(takeUntil(this.destroyed$))
            .subscribe(async (viewSetBinding: PropertyType | undefined) => {
                if (this.isViewSetBidingChanged(viewSetBinding) && this.stageEntityList.activeEntities.length > 0) {
                    const views: IMultistageAnatomicMap[] = await this.initAnatomicMapViews();
                    this.multistageConfig = this.createContainer(this.control.name, views, this.stagesColorData);
                    this._isLoading = false;
                } else {
                    this.multistageConfig = undefined;
                }
            });
    }

    private registerEcostressBinding() {
        this.modelService
            .registerList(this.control.dataBinding!, true)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(async (entity: CpEntityList | undefined) => {
                if (!entity) {
                    return;
                }
                this.stageEntityList = entity;
                this.createModelColorData();
                if (this.layoutConfig) {
                    const views: IMultistageAnatomicMap[] = await this.initEcostressMapViews();
                    this.multistageConfig = this.createContainer(this.control.name, views, this.stagesColorData);
                    this._isLoading = false;
                }
            });
    }

    private registerEcostressViewBinding() {
        this.modelService
            .registerList(this.control.viewBinding!, true)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(async (entity: CpEntityList | undefined) => {
                if (!entity) {
                    // gestire il caso vuoto
                    return;
                }
                this.viewsEntityList = entity as CpEntityList;
                if (this.layoutConfig) {
                    const views: IMultistageAnatomicMap[] = await this.initEcostressMapViews();
                    this.multistageConfig = this.createContainer(this.control.name, views, this.stagesColorData);
                    this._isLoading = false;
                }
            });
    }

    private registerEcostressViewSetBinding() {
        this.modelService
            .register(this.control.viewSetBinding || new DataBinding())
            .pipe(takeUntil(this.destroyed$))
            .subscribe(async (viewSetBinding: PropertyType | undefined) => {
                if (viewSetBinding == null) {
                    return;
                }
                // this.currentView = this.isApicalVisibleBoolToLabel(viewSetBinding as boolean);
                if (this.isViewSetBidingChanged(viewSetBinding)) {
                    if (this.control.viewSetBinding) {
                        this.currentViewSet = this.isApicalVisibleBoolToLabel(
                            this.modelService.getValue(this.control.viewSetBinding) as boolean | string | number,
                        );
                    }

                    if (this.layoutConfig) {
                        const views: IMultistageAnatomicMap[] = await this.initEcostressMapViews();
                        this.multistageConfig = this.createContainer(this.control.name, views, this.stagesColorData);
                        this._isLoading = false;
                    }
                }
            });
    }
    private setViewLabels() {
        if (!isDataBindingProperty(this.control.viewTopLabel) && typeof this.control.viewTopLabel === 'string') {
            this._viewTopLabel = this.control.viewTopLabel;
        }
        if (!isDataBindingProperty(this.control.viewBottomLabel) && typeof this.control.viewBottomLabel === 'string') {
            this._viewBottomLabel = this.control.viewBottomLabel;
        }
    }

    private registerToCpEffect() {
        if (this.control.stageViewImageBinding != undefined || this.control.legendImageBinding != undefined) {
            this.cpEffects.on('collectImages', this.saveHandlerWrapper.bind(this));
        }
    }

    /**
     * Gestisce la casistica in cui currentView è la stringa 'default'
     * @param viewSetBinding
     * @returns
     */
    private isViewSetBidingChanged(viewSetBinding: PropertyType | undefined): boolean | string {
        if (viewSetBinding == null) {
            return true;
        }
        return viewSetBinding.toString() !== this.currentViewSet;
    }

    // TODO controllare sia !!this.control.mapViews che !!this.control.viewsSets
    private async initAnatomicMapViews(): Promise<IMultistageAnatomicMap[]> {
        let viewsPromise: Promise<IMultistageAnatomicMap[]>;
        // imposto la nuova currentView
        this.currentViewSet = 'default';
        if (this.control.viewSetBinding) {
            this.currentViewSet = this.isApicalVisibleBoolToLabel(
                this.modelService.getValue(this.control.viewSetBinding) as boolean | string | number,
            );
        }
        // recurepo per questa currentView la lista di View desiderate
        const viewSets: string[] =
            this.control.viewsSets![this.currentViewSet as string] ?? this.control.viewsSets!['default'];
        // genero le View desiderate recuperando il .json  e creando la collezione di Viste IAnatomiMap
        // ordinandole per la proprieta definita
        const anatomicMapStream: Observable<IMultistageAnatomicMap>[] = [];
        const stageEntitiesListOrdered: ICpEntity[] = this.sortStage(this.stageEntityList.activeEntities);
        stageEntitiesListOrdered.forEach((stageEntity: ICpEntity) => {
            const cpEntityStageID: string = stageEntity.getPropertyValue('Id') as string;
            const cpEntityDicomDescription: string = stageEntity.getPropertyValue(
                this.control.stageNameProperty.propertyName,
            ) as string;
            const mapLabels: { upperLabel?: string; lowerLabel?: string } = this.retriveMapLabels(stageEntity);
            const stagesAnatomicMap: Observable<IMultistageAnatomicMap>[] = viewSets.map((mapName) => {
                return this.mapLoaderService.load(mapName).pipe(
                    map((res: IAnatomicMap) => {
                        return {
                            stageID: cpEntityStageID,
                            stageDescription: cpEntityDicomDescription,
                            ...mapLabels,
                            ...res,
                        };
                    }),
                );
            });
            anatomicMapStream.push(...stagesAnatomicMap);
        });
        viewsPromise = lastValueFrom(forkJoin(anatomicMapStream));

        return viewsPromise;
    }

    private async initEcostressMapViews(): Promise<IMultistageAnatomicMap[]> {
        let viewsPromise: Promise<IMultistageAnatomicMap[]>;

        const stageViewEntities: { stage: ICpEntity | undefined; view: ICpEntity | undefined }[] =
            this.layoutConfig!.map((stagesViews: ILayoutConfiguration) => {
                const stageEntities: ICpEntity | undefined = !!stagesViews.StageId
                    ? this.findStageById(stagesViews.StageId)
                    : undefined;
                const viewEntities: ICpEntity | undefined = !!stagesViews.ViewId
                    ? this.findViewByIdUsStressViewID(stagesViews.ViewId)
                    : undefined;
                return {
                    stage: stageEntities,
                    view: viewEntities,
                };
            });

        const stagesAnatomicMap: Observable<IMultistageAnatomicMap>[] = stageViewEntities.map(
            (stageViewEntity: { stage: ICpEntity | undefined; view: ICpEntity | undefined }) => {
                let mapStream: Observable<IMultistageAnatomicMap>;
                if (!stageViewEntity.stage || !stageViewEntity.view) {
                    mapStream = of(this.emptyAnatomicMap());
                } else {
                    const stageDicomDescription: string = stageViewEntity.stage!.getPropertyValue(
                        this.control.stageNameProperty.propertyName,
                    ) as string;
                    const viewDicomDescription: string = stageViewEntity.view!.getPropertyValue(
                        this.control.stageNameProperty.propertyName,
                    ) as string;
                    let multistageMapPartial: Partial<IMultistageAnatomicMap> = {
                        stageID: stageViewEntity.stage!.Id,
                        stageDescription: stageDicomDescription,
                    };
                    const IdUsStressView: any = stageViewEntity.view!.getPropertyPrimitiveValue(
                        this.control.viewIdProperty!.propertyName,
                    ) as any;
                    if (IdUsStressView) {
                        const mapName: string = IdUsStressView!.Description as string;
                        const mapNameApicalSuffix = this.isApicalVisibleLabelToBool(this.currentViewSet)
                            ? '_17'
                            : '_16';
                        const mapLabels: { upperLabel?: string; lowerLabel?: string } = this.retriveMapLabels(
                            stageViewEntity.stage!,
                        );
                        mapStream = this.mapLoaderService.load(mapName + mapNameApicalSuffix).pipe(
                            map((res: IAnatomicMap) => {
                                const multistageMap: IMultistageAnatomicMap = {
                                    ...multistageMapPartial,
                                    ...res,
                                    ...mapLabels,
                                    viewId: viewDicomDescription,
                                };
                                return multistageMap;
                            }),
                        );
                    } else {
                        mapStream = of(this.emptyAnatomicMap());
                    }
                }
                return mapStream;
            },
        );

        viewsPromise = lastValueFrom(forkJoin(stagesAnatomicMap));

        return viewsPromise;
    }

    private retriveMapLabels(dataBindingEntity: ICpEntity): { upperLabel?: string; lowerLabel?: string } {
        const mapLabel: { upperLabel?: string; lowerLabel?: string } = {};

        if (isDataBindingProperty(this.control.viewTopLabel)) {
            mapLabel['upperLabel'] = dataBindingEntity.getPropertyPrimitiveValue(
                (this.control.viewTopLabel as DataBindingPropertyType).propertyName,
            ) as string;
        }
        if (isDataBindingProperty(this.control.viewBottomLabel)) {
            mapLabel['lowerLabel'] = dataBindingEntity.getPropertyPrimitiveValue(
                (this.control.viewBottomLabel as DataBindingPropertyType).propertyName,
            ) as string;
        }
        return mapLabel;
    }

    private emptyAnatomicMap(): IMultistageAnatomicMap {
        const multistageMap: IMultistageAnatomicMap = {
            stageID: undefined,
            viewId: undefined,
            segmentCollection: undefined,
        };
        return multistageMap;
    }

    private sortStage(stageEntities: ICpEntity[]): ICpEntity[] {
        return stageEntities.sort((a: ICpEntity, b: ICpEntity) => {
            const aOrderValue = a.getPropertyValue(this.control.stageOrderProperty.propertyName) as number;
            const bOrderValue = b.getPropertyValue(this.control.stageOrderProperty.propertyName) as number;
            return aOrderValue - bOrderValue;
        });
    }

    private createContainer(
        title: string,
        maps: IAnatomicMap[],
        multistageColorData: IMultistageColorData[],
    ): IMultiStageContainerConfig {
        return {
            id: title,
            name: title,
            maps: maps,
            multistageColorData: multistageColorData,
        };
    }

    private createModelColorData() {
        // // init stageColorData
        this.stagesColorData = [];

        // // Convert stage CpEntities into IMultistageColorData
        const fallbackColorId = this.colorLegend[0].colorID;
        this.stageEntityList.activeEntities.forEach((cpEntity: ICpEntity) => {
            const stageSegments: ICpEntity = cpEntity.getPropertyValue(
                this.control.stageSegments.propertyName,
            ) as ICpEntity;
            const regionValue: IMultistageColorData = this.parsers.parseRegionsValue(
                stageSegments,
                this.allRegionsDataDescription,
                this.colorLegend,
                fallbackColorId,
            );
            this.stagesColorData.push(regionValue);
        });
    }

    public onDataUpdate(newDatas: IMultistageColorData[]) {
        newDatas.forEach((newData: IMultistageColorData) => {
            this.onNewData(newData.colorData, newData.stageID);
        });
        const isModelUpdated = this.modelService.setValue(this.control.dataBinding, this.stageEntityList, {
            force: true,
        });
        if (newDatas.length > 0 && isModelUpdated) {
            this.edited.emit();
        }
    }

    public onNewData(newData: IColorData[], stageID: string): void {
        const entityIndexToUpdate: number | undefined = this.findStageIndexById(stageID);
        if (entityIndexToUpdate == undefined) {
            return;
        }

        const stageEntityToUpdate: ICpEntity = this.stageEntityList.activeEntities[entityIndexToUpdate];
        let entityToUpdate: ICpEntity = stageEntityToUpdate.getPropertyValue(
            this.control.stageSegments.propertyName,
        ) as ICpEntity;
        if (!!entityToUpdate) {
            const legendDatasource: IDataSourceItem[] | undefined = this.dataSourceService.getById(
                this.control.legendDataSource!,
            );
            newData.forEach((colorData: IColorData) => {
                const legendItemToUpdate: IDataSourceItem | undefined = legendDatasource?.find(
                    (item) => item.Id === colorData.colorID,
                );
                const newEntity: CpEntity = this.modelService.makeEntity(
                    legendItemToUpdate as any,
                    entityToUpdate.path,
                );
                entityToUpdate.setPropertyValue(colorData.regionID, newEntity);
            });
        }
    }

    private findStageById(stageID: string): ICpEntity | undefined {
        return this.stageEntityList.activeEntities.find((entity: ICpEntity) => {
            return (entity.getPropertyValue('Id') as string) === stageID;
        });
    }

    private findStageIndexById(stageID: string): number | undefined {
        return this.stageEntityList.activeEntities.findIndex((entity: ICpEntity) => {
            return (entity.getPropertyValue('Id') as string) === stageID;
        });
    }

    private findViewByIdUsStressViewID(IdUsStressViewID: string): ICpEntity | undefined {
        if (!this.viewsEntityList) {
            return undefined;
        }
        return this.viewsEntityList.activeEntities.find((entity: ICpEntity) => {
            const IdUsStressView: any = entity.getPropertyValue('Id');
            return IdUsStressView === IdUsStressViewID;
        });
    }

    private haveDataChanged(newData: IColorData[], oldData: IColorData[]): boolean {
        return !newData.every((newD, i) => oldData[i].colorID === newD.colorID);
    }

    private getStageViewImagePropertyValue(entity: ICpEntity): PropertyType | null {
        const propertyValue: PropertyType | null = entity.getPropertyValue(
            this.control.stageViewImageBinding?.propertyName!,
        );
        return propertyValue;
    }

    private isApicalVisibleBoolToLabel(val: boolean | string | number): string {
        if (val == null) return 'default';
        return val.toString();
    }

    private isApicalVisibleLabelToBool(val: string): boolean {
        switch (val) {
            case 'default':
                return false;
            case 'false':
                return false;
            case 'true':
                return true;
            default:
                return false;
        }
    }

    private ensureStageGuid(propertyValue: PropertyType, stageEntity: ICpEntity): string {
        // se c'è un guid uso quello
        if (!!propertyValue) {
            return propertyValue.toString();
        }
        // se è vuoto creo un nuovo Guid
        //genera Guid
        const imgGuid = new Guid().toString();
        // const propertyValue: PropertyType | null = entity.getPropertyValue(this.control.stageViewImageBinding?.propertyName!);
        this.setStageGuid(stageEntity, imgGuid);
        //al posto di this.modelService.setValue , vedere checkin del 12/11/2021
        // this.modelService.setValue(
        //     this.control.stageViewImageBinding || new DataBinding(),
        //     imgGuid,
        // );
        return imgGuid;
    }

    private setStageGuid(stageEntity: ICpEntity, imgGuid: string | null): void {
        stageEntity.setPropertyValue(this.control.stageViewImageBinding?.propertyName!, imgGuid);
    }

    private setLegendGuid(legendGuid: string | null): void {
        this.modelService.setValue(this.control.legendImageBinding || new DataBinding(), legendGuid);
    }

    private getlegendImageBindingProperty(): PropertyType | null {
        if (this.control.legendImageBinding) {
            const cpEntity: ICpEntity | undefined = this.modelService.getEntity(
                this.control.legendImageBinding?.entity,
            );
            const propertyValue: PropertyType | null = cpEntity
                ? cpEntity.getPropertyValue(this.control.legendImageBinding?.propertyName!)
                : null;
            return propertyValue;
        }
        return null;
    }

    private ensureLegendGuid(propertyValue: PropertyType): string {
        // se c'è un guid uso quello
        if (!!propertyValue) {
            return propertyValue.toString();
        }
        // se è vuoto creo un nuovo Guid
        //genera Guid
        const imgGuid = new Guid().toString();
        //al posto di this.modelService.setValue , vedere checkin del 12/11/2021
        this.setLegendGuid(imgGuid);
        return imgGuid;
    }

    private beforeSave() {
        if (!this.control.stageViewImageBinding || !this.control.stageViewImageBinding?.propertyName) {
            return;
        }
        this.stagesIDtoPrint = this.getStageIDToPrint();
        this.setCurrentGuidSToPrintList();
        this.stagesIDNotToPrint = this.getStageIDNotToPrint();
        this.setCurrentGuidSToDeleteList();
        this.removeImageGuidFromStage();
    }

    private setCurrentGuidSToPrintList() {
        this.guidS = [];
        if (!this.stagesIDtoPrint || this.stagesIDtoPrint.length === 0) {
            return;
        }

        const stageEntityToPrint: ICpEntity[] = this.stageEntityList.activeEntities.filter((cpEntity: ICpEntity) => {
            const isStageToPrint = this.stagesIDtoPrint!.some((idToPrint) => {
                return (cpEntity.getPropertyValue('Id') as string) === idToPrint;
            });
            return !!isStageToPrint;
        });

        stageEntityToPrint.forEach((stageEntity: ICpEntity) => {
            const stageImageIdStored = this.getStageViewImagePropertyValue(stageEntity);

            const mapGuid = this.ensureStageGuid(stageImageIdStored, stageEntity);
            if (mapGuid) {
                this.guidS.push({
                    type: ImageType.IMAGE_MAP,
                    guid: mapGuid,
                    stageID: stageEntity.getPropertyValue('Id') as string,
                });
            }
        });

        const legendImageIdStored = this.getlegendImageBindingProperty();
        const legendGuid = this.ensureLegendGuid(legendImageIdStored);
        if (legendGuid) {
            this.guidS.push({ type: ImageType.IMAGE_LEGEND, guid: legendGuid, stageID: ImageType.IMAGE_LEGEND });
        }
    }

    private setCurrentGuidSToDeleteList() {
        this.guidSToDelete = [];
        if (!this.stagesIDNotToPrint || this.stagesIDNotToPrint.length === 0) {
            return;
        }
        const stageEntityToNotPrint: ICpEntity[] = this.stageEntityList.activeEntities.filter((cpEntity: ICpEntity) => {
            const isStageToDelete = this.stagesIDNotToPrint!.some((idToPrint) => {
                return (cpEntity.getPropertyValue('Id') as string) === idToPrint;
            });
            return !!isStageToDelete;
        });
        stageEntityToNotPrint.forEach((stageEntity: ICpEntity) => {
            const imgGuidIdToDelete = this.getStageViewImagePropertyValue(stageEntity);

            if (imgGuidIdToDelete) {
                this.guidSToDelete.push({
                    type: ImageType.IMAGE_MAP,
                    guid: imgGuidIdToDelete.toString(),
                    stageID: stageEntity.getPropertyValue('Id') as string,
                });
            }
        });
    }

    private removeImageGuidFromStage() {
        const stageEntityToNotPrint: ICpEntity[] = this.stageEntityList.activeEntities.filter((cpEntity: ICpEntity) => {
            const isStageToDelete = this.stagesIDNotToPrint!.some((idToPrint) => {
                return (cpEntity.getPropertyValue('Id') as string) === idToPrint;
            });
            return !!isStageToDelete;
        });
        stageEntityToNotPrint.forEach((stageEntity: ICpEntity) => {
            this.setStageGuid(stageEntity, null);
        });

        const isAllNotToPrint: boolean = this.stageEntityList.activeEntities.every((entity:ICpEntity) => {
                return !entity.getPropertyValue(this.control.stageIsToPrint!.propertyName)
            });
        if(isAllNotToPrint){
            const legendImageIdStored = this.getlegendImageBindingProperty();
            if(legendImageIdStored){
                this.guidSToDelete.push({ type: ImageType.IMAGE_LEGEND, guid: legendImageIdStored.toString() });
                this.setLegendGuid(null);
            }
        }
    }

    private prepareSaveHandler(): ObservableOrAwaitable<EventResult> {
        this.beforeSave();
        const guidSToPrintPresent: boolean = !!this.guidS && this.guidS.length > 0;
        const guidSToDeletePresent: boolean = !!this.guidSToDelete && this.guidSToDelete.length > 0;
        // se ci sono stage da stampare creo effect e poi provo ad aggiungere effect di cancellazione
        if (guidSToPrintPresent) {
            return this.multistagecontainerComponent
                .getImages(this.guidS)
                .then((imageWrapper: RasterImgWrapper[]) => {
                    let eventResult: EffectResultImage[];
                    eventResult = this.guidS.map((item: IGuid) => {
                        const requested: RasterImgWrapper | undefined = imageWrapper.find(
                            (rastImgWrap: RasterImgWrapper) => {
                                return rastImgWrap.getImg().stageID === item.stageID;
                            },
                        );
                        const result: EffectResultImage = {
                            type: 'image',
                            id: item.guid,
                            data: requested!.getImg().base64,
                        };

                        return result;
                    });
                    return eventResult;
                })
                .then((eventResult: EffectResult[]) => {
                    if (!guidSToDeletePresent) {
                        return eventResult;
                    }
                    const resultImgDelete: EffectResultImageDelete[] = this.guidSToDelete.map((itemToDelete: IGuid) => {
                        const result: EffectResultImageDelete = {
                            type: 'image-delete',
                            id: itemToDelete.guid,
                        };
                        return result;
                    });
                    return [...eventResult, ...resultImgDelete];
                });
        }

        // se non ci sono stage da stampare, ma ci sono stage da cancellare
        if (guidSToDeletePresent) {
            const resultImgDelete: EffectResultImageDelete[] = this.guidSToDelete.map((itemToDelete: IGuid) => {
                const result: EffectResultImageDelete = {
                    type: 'image-delete',
                    id: itemToDelete.guid,
                };
                return result;
            });
            return Promise.resolve(resultImgDelete);
        }
        return EMPTY;
    }

    private getStageIDToPrint(): string[] | undefined {
        if (!this.control.stageIsToPrint?.propertyName) {
            return [];
        }
        const toPrintCpEntity: ICpEntity[] | undefined = this.stageEntityList.activeEntities.filter(
            (entity: ICpEntity) => {
                const isToPrint: boolean = entity.getPropertyValue(
                    this.control.stageIsToPrint!.propertyName,
                ) as boolean;
                return isToPrint === true && entity.isModified === true;
            },
        );
        return toPrintCpEntity?.map((entity: ICpEntity) => {
            return entity.Id;
        });
    }

    private getStageIDNotToPrint(): string[] | undefined {
        if (!this.control.stageIsToPrint?.propertyName) {
            return [];
        }
        const imgToDeleteCpEntity: ICpEntity[] | undefined = this.stageEntityList.activeEntities.filter(
            (entity: ICpEntity) => {
                const isToPrintValue: boolean = entity.getPropertyValue(this.control.stageIsToPrint!.propertyName) as boolean;
                const currentGuid: PropertyType | null = this.getStageViewImagePropertyValue(entity);
                return entity.isModified === true && isToPrintValue === false && currentGuid != null;
            },
        );
        return imgToDeleteCpEntity?.map((entity: ICpEntity) => {
            return entity.Id;
        });
    }

    private saveHandlerWrapper(): ObservableOrAwaitable<EventResult> {
        return this.prepareSaveHandler();
    }

    // public _prepareSaveHandlerDebug(){
    //     this.prepareSaveHandler();
    // }

    private initMapContainerGraphicLayout() {
        this._viewsSideLenght = this.control.getHeightHint();
        this.fixedColumnNumber = this.control.fixedViewsColNumber ?? 0;
        // this.calculateMaxGridDimension();
    }

    /**
     *  Metodo esposto dal componente per configurare il componente ecostress
     * @param config configurazione passata dal viewer
     */
    public async setLayoutConfig(config: IUserSelection) {
        if (config.fixedLayout) {
            this.fixedLayout = config.fixedLayout;
        }
        this.layoutConfig = config.layoutConfig;
        if (this.layoutConfig) {
            const views: IMultistageAnatomicMap[] = await this.initEcostressMapViews();
            this.multistageConfig = this.createContainer(this.control.name, views, this.stagesColorData);
            this._isLoading = false;
        }
        if (config.requestEmitEdit) {
            this.edited.emit();
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.containerSub) {
            this.containerSub.unsubscribe();
        }
        this.cpEffects.off('collectImages', this.saveHandlerWrapper.bind(this));
    }
}
